import { FormEvent, useCallback, useState } from 'react';

import { Button, Field, Input, Label, Switch } from '@genesis/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import z from 'zod';

import { cookiesKeys } from '@/app/constants/cookiesKeys';
import { getAfterAuthRoute } from '@/app/utils/auth/getAfterAuthRoute';
import { Logo } from '@/common/components/modules/logo';
import { secureStorage } from '@/infra/protocols/storage';

const Login = () => {
  const navigate = Route.useNavigate();

  const [rememberMe, setRememberMe] = useState(() => {
    const remember = secureStorage.get(cookiesKeys.REMEMBER_EMAIL) ?? 'false';
    return remember !== 'false';
  });

  const [email, setEmail] = useState(() => {
    const email = secureStorage.get(cookiesKeys.USER_EMAIL) ?? '';
    return email ?? '';
  });

  const onSubmitLoginForm = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (rememberMe) {
        secureStorage.add(cookiesKeys.USER_EMAIL, `${email}`);
      }
      navigate({ to: '/auth' });
    },
    [rememberMe, navigate, email],
  );

  const onChangeRememberMe = useCallback((value: boolean) => {
    setRememberMe(value);
    if (!value) {
      secureStorage.remove(cookiesKeys.USER_EMAIL);
    }
    secureStorage.add(cookiesKeys.REMEMBER_EMAIL, `${value}`);
  }, []);

  const onChangeEmailInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  return (
    <div className='flex w-screen h-screen'>
      <div className='flex w-full h-full sm:grid sm:grid-cols-2'>
        <div className='flex w-full h-full items-center justify-center p-12'>
          <form
            className='flex flex-col w-full gap-6 items-center max-w-lg'
            onSubmit={onSubmitLoginForm}
          >
            <Logo />

            <div className='flex flex-col items-center w-full gap-4'>
              <Field className='w-full flex-col gap-2'>
                <Label className='text-sm'>Email</Label>
                <Input
                  name='email'
                  value={email}
                  onChange={onChangeEmailInput}
                  placeholder='exemplo@g.globo'
                />
              </Field>

              <Field className='w-full gap-2'>
                <Switch onChange={onChangeRememberMe} checked={rememberMe} />
                <Label>Lembrar de mim</Label>
              </Field>
            </div>

            <Button variant='primary' className='flex w-full' disabled={!email}>
              Entrar
            </Button>
          </form>
        </div>

        <div className='hidden h-full sm:flex'>
          <img
            loading='eager'
            decoding='async'
            fetchPriority='high'
            src='/images/banner.webp'
            className='object-cover max-h-screen w-full'
            alt='Microfone profissional em um fundo preto meio iluminado.'
          />
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_auth/entrar')({
  validateSearch: z.object({
    redirect: z.string().optional().catch(''),
  }),
  component: Login,
  beforeLoad: ({ context, search }) => {
    if (context.auth.signedIn) {
      throw redirect({ to: search.redirect ?? getAfterAuthRoute() });
    }
  },
});
